import { useState } from 'react';
import {
  AlertTriangle,
  Loader2,
  CheckCircle,
  Database,
  Plus,
  Trash2,
} from 'lucide-react';
import { useUpdateMeridianLinkLoanFragment } from '../../hooks/useLosProxy';

const COMMAND_TYPES = [
  { value: 'CONTACTS', label: 'Contacts', description: 'Appraiser, Flood, Title agent info' },
  { value: 'PROPERTY_INFO', label: 'Property Info', description: 'Valuation, tax, vesting, HMDA data' },
  { value: 'LOAN_INFO', label: 'Loan Info', description: 'Loan amounts, LTV, rates' },
];

interface CommandEntry {
  type: string;
  data: string;
}

interface Props {
  loanNumber: string;
}

export default function MeridianLinkForceUpdate({ loanNumber }: Props) {
  const [commands, setCommands] = useState<CommandEntry[]>([{ type: 'PROPERTY_INFO', data: '{}' }]);
  const [showConfirm, setShowConfirm] = useState(false);
  const updateFragment = useUpdateMeridianLinkLoanFragment(loanNumber);

  if (!loanNumber) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">No loan number available</p>
      </div>
    );
  }

  const addCommand = () => {
    setCommands([...commands, { type: 'PROPERTY_INFO', data: '{}' }]);
  };

  const removeCommand = (index: number) => {
    setCommands(commands.filter((_, i) => i !== index));
  };

  const updateCommand = (index: number, field: 'type' | 'data', value: string) => {
    const updated = [...commands];
    updated[index] = { ...updated[index], [field]: value };
    setCommands(updated);
  };

  const handleSubmit = () => {
    const parsedCommands = commands.map((cmd) => {
      try {
        return { type: cmd.type, ...JSON.parse(cmd.data) };
      } catch {
        return { type: cmd.type };
      }
    });

    updateFragment.mutate(
      { commands: parsedCommands },
      {
        onSuccess: () => {
          setShowConfirm(false);
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Database className="h-4 w-4 text-teal-400" />
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Push Data to MeridianLink</h4>
      </div>

      <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-500/20">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-amber-700 dark:text-amber-300">Caution</p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
              This will directly update the loan in MeridianLink. If the borrower is currently active
              in the application, the update will be deferred (DEFERRED status).
            </p>
          </div>
        </div>
      </div>

      {updateFragment.isSuccess && (
        <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-500/20">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <p className="text-xs text-green-700 dark:text-green-300">Loan fragment updated successfully</p>
          </div>
        </div>
      )}

      {updateFragment.isError && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-500/20">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <p className="text-xs text-red-700 dark:text-red-300">
              Failed: {(updateFragment.error as Error).message}
            </p>
          </div>
        </div>
      )}

      {/* Commands */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Commands</p>
          <button onClick={addCommand} className="text-[11px] text-blue-500 hover:text-blue-400 flex items-center gap-1">
            <Plus className="h-3 w-3" /> Add command
          </button>
        </div>

        {commands.map((cmd, index) => (
          <div key={index} className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2">
            <div className="flex items-center gap-2">
              <select
                value={cmd.type}
                onChange={(e) => updateCommand(index, 'type', e.target.value)}
                className="text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-gray-700 dark:text-gray-300 flex-1"
              >
                {COMMAND_TYPES.map((ct) => (
                  <option key={ct.value} value={ct.value}>
                    {ct.label} - {ct.description}
                  </option>
                ))}
              </select>
              {commands.length > 1 && (
                <button onClick={() => removeCommand(index)} className="btn-icon p-1" title="Remove">
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </button>
              )}
            </div>
            <textarea
              value={cmd.data}
              onChange={(e) => updateCommand(index, 'data', e.target.value)}
              placeholder='{"key": "value"}'
              className="w-full text-[11px] rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-gray-700 dark:text-gray-300 resize-none h-20 font-mono"
            />
          </div>
        ))}
      </div>

      {/* Submit */}
      {!showConfirm ? (
        <button
          onClick={() => setShowConfirm(true)}
          disabled={commands.length === 0}
          className="btn-primary text-xs gap-1.5 w-full justify-center disabled:opacity-50"
        >
          <Database className="h-3.5 w-3.5" />
          Push Data to Loan
        </button>
      ) : (
        <div className="p-3 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-950/20 space-y-3">
          <p className="text-xs font-medium text-red-700 dark:text-red-300">
            Confirm: Push {commands.length} command(s) to loan {loanNumber}?
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={updateFragment.isPending}
              className="btn-danger text-xs gap-1.5 flex-1 justify-center"
            >
              {updateFragment.isPending ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Pushing...</>
              ) : (
                'Confirm'
              )}
            </button>
            <button onClick={() => setShowConfirm(false)} className="btn-secondary text-xs flex-1 justify-center">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
